'use client'
import { Link } from '@/i18n/routing'
import { getJobHref } from '@/utilities/getJobHref'
import { formatDateTime } from '@/utilities/formatDateTime'
import RichText from '@/components/RichText'
import { useTranslations } from 'next-intl'
import type { JobOffer } from '@/payload-types'

interface JobCardProps {
  job: JobOffer
  locale: string
}

export function JobCard({ job, locale }: JobCardProps) {
  const t = useTranslations('jobs')
  const formattedDate = formatDateTime({
    date: job.createdAt,
    pattern: 'PPP',
    locale: locale === 'cs' ? 'cs-CZ' : 'en-US',
  })

  return (
    <Link href={getJobHref(job, locale as 'en' | 'cs')} className="block group">
      <div className="relative bg-black/40 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden hover:border-strandforge-purple/50 transition-all duration-300 hover:shadow-2xl hover:shadow-strandforge-purple/20">
        <div className="absolute inset-0 bg-gradient-to-br from-strandforge-purple/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="relative p-8">
          <h2 className="text-2xl font-bold mb-4 text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-strandforge-purple-dark group-hover:via-strandforge-purple group-hover:to-strandforge-purple-light group-hover:bg-clip-text transition-all duration-300">
            {job.title}
          </h2>

          {job.summary && (
            <div className="text-gray-300 mb-6 prose prose-invert prose-sm max-w-none">
              <RichText data={job.summary} enableGutter={false} enableProse={false} />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {job.category && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-strandforge-purple-light uppercase tracking-wider">
                  {t('listing.category')}
                </h3>
                <p className="text-white font-medium">{job.category}</p>
              </div>
            )}

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-strandforge-purple-light uppercase tracking-wider">
                {t('listing.postedOn')}
              </h3>
              <p className="text-white font-medium">{formattedDate}</p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="inline-flex items-center text-strandforge-purple-light group-hover:text-white transition-colors">
              <span className="mr-2 font-medium">{t('listing.readMore')}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 transform group-hover:translate-x-1 transition-transform"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                window.location.href = `mailto:jobs@strandforge.com?subject=Application for: ${job.title}`
              }}
              className="bg-gradient-to-r from-strandforge-purple to-strandforge-purple-light hover:from-strandforge-purple-dark hover:to-strandforge-purple text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-strandforge-purple/30 hover:scale-105"
            >
              {t('detail.contact')}
            </button>
          </div>
        </div>
      </div>
    </Link>
  )
}
